import React, { useEffect, useMemo, useState } from 'react';
import debounce from 'lodash/debounce';
import { Dropdown } from '@axiom/ui';
import type { CompanySearchResult } from '@axiom/validation';

import { LogoDevApi } from '../../api/logo-dev-api';

export const getLabel = (name?: string | null, domain?: string | null) =>
  domain && name ? `${name} - ${domain}` : name;

export const CompanyTypeahead = ({
  initialValue = {},
  label,
  name,
  onChange,
}: {
  initialValue?: CompanySearchResult;
  label: string;
  name: string;
  onChange: (payload: CompanySearchResult) => void;
}) => {
  const initialAutoComplete = initialValue.domain
    ? []
    : [
        {
          label: getLabel(initialValue.name, initialValue.domain),
          value: getLabel(initialValue.name, initialValue.domain),
          payload: {
            name: initialValue.name,
            domain: initialValue.domain,
          },
        },
      ];
  const [autoCompletions, setAutoCompletions] = useState<
    Array<{
      label?: string | null;
      value?: string | null;
      payload: CompanySearchResult;
    }>
  >(initialAutoComplete);

  const fetchCompanyResults = (value: string) => {
    LogoDevApi.readSearchResults(value).then(
      ({ data }) => {
        const formatted = data.map((ac: CompanySearchResult) => ({
          label: getLabel(ac.name, ac.domain),
          value: getLabel(ac.name, ac.domain),
          payload: { name: ac.name, domain: ac.domain },
        }));
        setAutoCompletions(formatted);
      },
      () => {}
    );
  };

  const onCompanyNameChange = useMemo(
    () => debounce(fetchCompanyResults, 100),
    []
  );

  useEffect(() => {
    if (initialValue.domain) {
      fetchCompanyResults(initialValue.domain);
    }
  }, [initialValue.domain]);

  const handleAddition = (value: string) => {
    setAutoCompletions([
      {
        label: value,
        value,
        payload: { name: value, domain: null },
      },
      ...autoCompletions,
    ]);
  };

  return (
    <div data-test="COMPANY_TYPEAHEAD">
      <Dropdown
        displayKey="label"
        label={label}
        name={name}
        onAddItem={value => {
          handleAddition(value as string);
        }}
        onType={value => {
          onCompanyNameChange(value as string);
        }}
        options={autoCompletions}
        onChange={(v, e, valueOption) => {
          const option = valueOption as Record<string, unknown>;
          if (valueOption) {
            onChange(option.payload as CompanySearchResult);
          } else {
            onChange({ name: v, domain: null } as CompanySearchResult);
          }
        }}
        valueKey="value"
      />
    </div>
  );
};
